<template>
  <div class="items-list-app h-100" :class="advancedView ? 'container-fluid' : 'container-xl'">
    <div class="row h-100">
      <div class="col h-100">
        <div class="h-100 d-flex flex-column">
          <div class="mb-3 pt-4 d-flex justify-content-between align-items-end">
            <!-- HEADER SECTION -->
            <div>
              <div class="d-flex gap-3 align-items-center">
                <h1>Contacts</h1>
                <b-link href="#" class="text-uppercase font-size-70" @click="toggleAdvancedView">
                  <b-icon icon="arrow-repeat"></b-icon>
                  {{
                    advancedView
                      ? "Switch to Basic View"
                      : "Switch to Advanced View"
                  }}
                </b-link>
              </div>
              <p class="lead mb-0">
                View and manage all contacts in your supply-chain.
              </p>
            </div>
            <div class="">
              <b-button variant="success" @click="handleAddNewContact">
                <b-icon icon="plus-circle" class="mr-1"></b-icon>
                Add Contact
              </b-button>
            </div>
          </div>

          <div v-if="isInitialAdvancedViewSet" class="w-100 flex-fill pb-4">
            <object-grid 
              v-if="advancedView" 
              ref="contactsObjectGrid" 
              model-class="Contact" 
              class="h-100 flex-fill"
              grid-max-height="100%" 
              :block-size="100" 
              col-state-local-storage-key="contact-list"
              :rowActions="rowActionsGetter"
              @view-contact="handleViewContact" 
              @action-click="handleObjectGridActionClick" 
            ></object-grid>
            <contact-list v-else ref="contactList"></contact-list>
          </div>
        </div>
      </div>
    </div>
    <contact-create-update-modal ref="contactModal" :redirect-after-create="false" hide-save-redirect
      @create-complete="refreshList" @update-complete="refreshList"></contact-create-update-modal>
  </div>
</template>

<script>
import { suppliosApi } from "@/js/api-service";
import { currentUserInfoMixin } from "@/js/custom-vue-mixins";


// const rowActionsGetter = (cellRendererParams) => {
//   // this gets passed down into the grid, takes the ag-grid cell renderer params object as single arg
//   // we can use this to determine what actions to show based on the row data, user permissions, etc.
//   // return should be an array of action objects { key, label, bsIcon, variant }
//   const rowData = cellRendererParams.data;
//   const actions = [
//     {
//       key: "view",
//       label: "View",
//       bsIcon: "eye",
//       variant: "primary",
//     },  // always have this one... sometimes have deactivate
//   ];
//   if (this.$currentUserInfo.is_tenant_admin && rowData.portal_access !== 'on_hold') {
//     // gotta figure out how to properly pass this down.... closure in component method?  Just pass down the component method?
//     actions.push({
//       key: "deactivate",
//       label: "Deactivate",
//       bsIcon: "trash",
//       variant: "danger",
//     });
//   }
//   return actions;
// };

export default {
  name: "AdvancedContactList",
  props: [],
  components: {},
  mixins: [currentUserInfoMixin],
  data() {
    return {
      advancedView: false,
      isInitialAdvancedViewSet: false,
      preferAdvancedLocalStorageKey: "contactListTypePreferAdvanced",
    };
  },
  computed: {
    rowActions() {
      // TODO - need to make this dynamic based on permissions
      // in contact-list, the logic is this:  v-if="$currentUserInfo.is_tenant_admin" :disabled="data.item.portal_access === 'on_hold'"
      return [
        {
          key: "view",
          label: "View",
          bsIcon: "eye",
          variant: "primary",
        },
        {
          key: "deactivate",
          label: "Deactivate",
          bsIcon: "trash",
          variant: "danger",
        },
      ];
    },
  },
  mounted() {
    this.getPreferAdvancedFromLocalStorage();
    this.isInitialAdvancedViewSet = true;
  },
  methods: {
    toggleAdvancedView() {
      this.advancedView = !this.advancedView;
      this.setPreferAdvancedInLocalStorage();
    },
    setPreferAdvancedInLocalStorage() {
      const key = this.preferAdvancedLocalStorageKey;
      const preferAdvanced = JSON.stringify(this.advancedView);
      localStorage.setItem(key, preferAdvanced);
    },
    getPreferAdvancedFromLocalStorage() {
      const preferAdvancedRaw = localStorage.getItem(
        this.preferAdvancedLocalStorageKey
      );
      if (preferAdvancedRaw) {
        this.advancedView = JSON.parse(preferAdvancedRaw);
      }
    },
    clearPreferAdvancedFromLocalStorage() {
      localStorage.removeItem(this.preferAdvancedLocalStorageKey);
    },
    handleViewContact(itemId) {
      this.$refs.contactModal.startView(itemId);
    },
    handleAddNewContact() {
      this.$refs.contactModal.startCreate();
    },
    refreshList() {
      if (this.advancedView) {
        this.$refs.contactsObjectGrid.reloadRows();
        this.initialLoading = false;
        return;
      } else {
        this.$refs.contactList.refreshList();
      }
    },
    async startDeactivate(tuObj) {
      // show modal to warn about deactivating, then if confirmed, call the real one
      const tuFullName = tuObj.full_name || 'this user';
      const confirmOk = await this.$bvModal.msgBoxConfirm(`Are you sure you want to deactivate ${tuFullName}?  This is will remove them from your contact database, and prevent them from logging in (if they had access).`, {
        title: `Deactivate ${tuFullName}?`,
        size: 'sm',
        buttonSize: 'sm',
        okVariant: 'danger',
        okTitle: 'Yes, Deactivate',
        cancelTitle: 'No, Cancel',
        footerClass: 'p-2',
        hideHeaderClose: false,
        centered: true
      });
      if (confirmOk) {
        try {
          const { data } = await suppliosApi.contact.deactivate(tuObj.id);
          // data is just a string message saying person was deleted
          this.$toast.success(data);
          this.refreshList();  // fire and forget to reload the list
        } catch (err) {
          this.handleError(err, 'Error while deactivating this user.');
        }
      }
    },
    handleObjectGridActionClick(actionObj, rowData) {
      // actionObj is the action object that was clicked
      // actionCellParams is the ag-grid ICellRendererParams for that action cell { data, node, colDef, etc. }
      const tuId = rowData.id;
      const actionKey = actionObj.key;
      if (actionKey === "view") {
        this.handleViewContact(tuId);
      } else if (actionKey === "deactivate") {
        this.startDeactivate(rowData);
      }
    },
    rowActionsGetter(cellRendererParams) {
      // should we pass THIS method down?  or return the getter function that gets passed down? ???
      // THIS WORKS!  Just passing down the method from the component instance...
      // we could just build the function here based on the current user being admin or not... then pass down that function... ?
      // this gets passed down into the grid, takes the ag-grid cell renderer params object as single arg
      // we can use this to determine what actions to show based on the row data, user permissions, etc.
      // return should be an array of action objects { key, label, bsIcon, variant }
      const rowData = cellRendererParams.data;
      const actions = [
        {
          key: "view",
          label: "View",
          bsIcon: "eye",
          variant: "primary",
        },  // always have this one... sometimes have deactivate
      ];
      if (this.$currentUserInfo.is_tenant_admin && rowData.portal_access !== 'on_hold') {
        actions.push({
          key: "deactivate",
          label: "Deactivate",
          bsIcon: "trash",
          variant: "danger",
        });
      }
      return actions;
    },
  },
};
</script>
